import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';

/**
 * The one denormalized inbox preview string a recall ever writes, on both
 * the modern and legacy identity branches (see recallMessage in
 * messageRecallWrite.ts) and the only string the deployed Firestore rule
 * `expectedInboxPreview` accepts for a recalled message
 * (shared/firestore.rules). One constant so the write side and the read
 * side (threadInboundActivityMs in messageInboxStateCore.ts, D7) can never
 * drift apart.
 */
export const RECALLED_THREAD_PREVIEW_TEXT = 'Message recalled';

/**
 * The human-facing preview: what a person should READ.
 *
 * Not what goes in `threads/{id}.lastMessageText`. That field is bound by the
 * rules (see threadInboxPreviewText below), and every reader of it already
 * runs plainMessageText, so the two are allowed to differ and do.
 */
export function messagePreviewText(text: string, attachments: readonly unknown[]): string {
  const visibleText = plainMessageText(text);
  if (visibleText.length > 0 && visibleText.length <= 200) return visibleText;
  if (visibleText.length > 200) return 'Long message. Open to read.';
  if (attachments.length > 0) return 'Attachment';
  return '';
}

/**
 * What `threads/{id}.lastMessageText` MUST contain, byte for byte.
 *
 * This is a faithful copy of `expectedInboxPreview()` in
 * shared/firestore.rules, which measures the STORED text and, at 200 stored
 * characters or fewer, requires the preview to equal that text verbatim.
 *
 * It exists because the visible-text preview and the rule disagreed the moment
 * a message could be stored as a document. Measured in the emulator: a
 * formatted "Hi" is 125 stored characters, so the rule compares verbatim, and
 * a client writing the visible "Hi" was REFUSED - the message did not send at
 * all. Writing this value is accepted.
 *
 * The rule is the cheaper end to leave alone: teaching it to parse a document
 * costs content bytes, and the artifact has 214 to spend. Nothing is lost by
 * storing the document here, because every reader of lastMessageText
 * (ThreadRow, Search, the Messages search haystack) already renders it through
 * plainMessageText.
 *
 * `recalled` is the caller's business, same as in the rule: a recall writes
 * 'Message recalled' directly.
 */
export function threadInboxPreviewText(text: string, attachments: readonly unknown[]): string {
  if (text.length > 0) return text.length <= 200 ? text : 'Long message. Open to read.';
  if (attachments.length > 0) return 'Attachment';
  return '';
}

export function previewMatchesOriginalMessage(
  preview: string | null | undefined,
  text: string
): boolean {
  return preview === messagePreviewText(text, [])
    || (plainMessageText(text).length > 200 && preview === plainMessageText(text).slice(0, 200))
    || (plainMessageText(text).length === 0 && [
      'Attachment',
      'GIF attached',
      'Video attached',
      'Audio attached',
      'File attached',
      'Photo attached',
    ].includes(preview ?? ''));
}

interface LegacyThreadPreviewIdentity {
  lastMessageId?: string | null;
  lastMessageBy?: string | null;
  lastMessageAt?: unknown;
}

interface LegacyMessagePreviewIdentity {
  authorUid?: string | null;
  createdAt?: unknown;
}

function timestampMillis(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return null;
  try {
    const millis = toMillis.call(value);
    return typeof millis === 'number' && Number.isFinite(millis) ? millis : null;
  } catch {
    return null;
  }
}

/**
 * Legacy threads predate `lastMessageId`. Match their current preview to a
 * message using immutable author + server timestamp metadata, never preview
 * text (two messages can legitimately have identical text).
 */
export function legacyMessageOwnsPreview(
  thread: LegacyThreadPreviewIdentity,
  message: LegacyMessagePreviewIdentity
): boolean {
  if (typeof thread.lastMessageId === 'string' && thread.lastMessageId.length > 0) return false;
  if (!message.authorUid || thread.lastMessageBy !== message.authorUid) return false;
  const threadMillis = timestampMillis(thread.lastMessageAt);
  const messageMillis = timestampMillis(message.createdAt);
  return threadMillis !== null && messageMillis !== null && threadMillis === messageMillis;
}
